import { yupResolver } from '@hookform/resolvers/yup'
import { map } from 'lodash-es'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import {
  FestivalEdition,
  MigrationConfig,
} from '../../../../common/models/dates-settings'
import FormGroup from '../../../../core/components/FormGroup'
import { formatDateTime } from '../../../../core/constants/times'
import { EditionChange } from '../../../../core/context/EditionChangeContext'
import {
  createEditions,
  deleteFestivalEdition,
  getEditions,
  publishFestivalEdition,
  updateEditionSingelEdit,
} from '../../../../core/services/festival-settings'
import { DeleteConfirmationModal } from '../../DeleteConfirmationModal'
import { MessageBox } from '../../messages/MessageBox'
import { Button, Card, Checkbox, Input } from '@/components/ui'
import { DateTimePicker } from '@/components/ui/date-time-picker'

interface IEditionsValues {
  editions: {
    eId: string
    name: string
    active: boolean
    backstageEventCode: string
    startDate: string
    endDate: string
  }[]
}

export interface IEditionsValuesCreate {
  name: string
  migrationConfig: MigrationConfig
  autoActivate: boolean
  activateDate?: string | null
  startDate: string
  endDate: string
  backstageEventCode: string
}

export const Editions: React.FC<{ hasEditPermission: boolean }> = ({
  hasEditPermission,
}) => {
  const { editionId, festivalName } = useParams()

  const [editions, setEditions] = useState<FestivalEdition[]>()
  const [openIndex, setOpenIndex] = useState<string>()
  const { setEditionChanged } = useContext(EditionChange)
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [deleteId, setDeleteId] = useState<string>()

  const getData = useCallback(async () => {
    // prettier-ignore
    (async () => {
      try {
        const resp = await getEditions()
        setEditions(resp.data)
        setTimeout(() => {
          const temp = map(resp.data, (edition) => {
            return {
              eId: edition.id,
              name: edition.name,
              backstageEventCode: edition.backstageEventCode,
              active: edition.active,
              startDate: formatDateTime(new Date(edition.startDate).toISOString()),
              endDate: formatDateTime(new Date(edition.endDate).toISOString())
            }
          })

          setValueEdit(`editions`, temp)
        }, 200)

      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  useEffect(() => {
    getData()
  }, [getData])

  const schemaEdit = yup.object().shape({
    editions: yup
      .array()
      .of(
        yup.object().shape({
          eId: yup.string().required(),
          name: yup.string().required(),
          active: yup.boolean().required(),
          startDate: yup.string().required(),
          endDate: yup.string().required(),
          backstageEventCode: yup.string().required(),
        }),
      )
      .required(),
  })

  const {
    register: registerEdit,
    formState: { errors: errorsEdit },
    setValue: setValueEdit,
    trigger: triggerEddit,
    watch: watchEdit,
    control: controlEdit,
  } = useForm<IEditionsValues>({
    mode: 'onChange',
    resolver: yupResolver<IEditionsValues>(schemaEdit),
    defaultValues: {},
  })

  const { fields: editionEditFields } = useFieldArray({
    control: controlEdit,
    name: 'editions',
  })

  const submitEdit = async (index: number) => {
    const isValid = await triggerEddit(`editions.${index}`)
    if (!isValid) return
    const edit = watchEdit(`editions.${index}`)

    try {
      if (edit) {
        const data = {
          id: edit.eId,
          name: edit.name,
          startDate: new Date(edit.startDate).valueOf(),
          endDate: new Date(edit.endDate).valueOf(),
          active: edit.active,
          backstageEventCode: edit.backstageEventCode,
        }
        const { status } = await updateEditionSingelEdit(edit.eId, data)
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const handlePublish = async (id: string) => {
    try {
      const resp = await publishFestivalEdition(id)
      if (resp.status === 200) {
        getData()
        setEditionChanged(true)
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Publish Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const deleteFunc = async () => {
    try {
      if (!deleteId) return
      const { status } = await deleteFestivalEdition(deleteId)
      getData()
      if (status === 204) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Delete Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = (id: string) => {
    const temp = map(editions, (edition, index) => {
      if (edition.id === id) {
        return {
          eId: edition.id,
          name: edition.name,
          backstageEventCode: edition.backstageEventCode,
          active: edition.active,
          startDate: new Date(edition.startDate).toISOString().split('T')[0],
          endDate: new Date(edition.endDate).toISOString().split('T')[0],
        }
      }
      return {
        eId: editionEditFields?.[index]?.id,
        name: editionEditFields?.[index]?.name,
        backstageEventCode: editionEditFields?.[index]?.backstageEventCode,
        active: editionEditFields?.[index]?.active,
        startDate: editionEditFields?.[index]?.startDate,
        endDate: editionEditFields?.[index]?.endDate,
      }
    })
    setValueEdit(`editions`, temp)
    setOpenIndex('')
  }

  const schemaCreate = yup.object().shape({
    name: yup.string().required(),
    migrationConfig: yup.object().shape({
      migrateInfos: yup.boolean().required(),
      migratePerformances: yup.boolean().required(),
      migratePartners: yup.boolean().required(),
      migrateTicketCategories: yup.boolean().required(),
      migrateExtras: yup.boolean().required(),
      migrateVideos: yup.boolean().required(),
      migrateImages: yup.boolean().required(),
    }),
    autoActivate: yup.boolean().required(),
    activateDate: yup.string().when('autoActivate', {
      is: (val: string) => (val ? true : false),
      then: (schema) => schema.optional().nullable(),
      otherwise: (schema) => schema.required(),
    }),
    startDate: yup.string().required(),
    endDate: yup.string().required(),
    backstageEventCode: yup.string().required(),
  })

  const {
    handleSubmit: handleSubmitCreate,
    register: registerCreate,
    formState: { errors: errorsCreate },
    watch: watchCreate,
    reset: resetCreate,
    setValue: setValueCreate,
  } = useForm<IEditionsValuesCreate>({
    mode: 'onChange',
    resolver: yupResolver<IEditionsValuesCreate>(schemaCreate),
    defaultValues: {
      migrationConfig: {
        migrateInfos: true,
        migratePerformances: true,
        migratePartners: true,
        migrateTicketCategories: true,
        migrateExtras: true,
        migrateVideos: true,
        migrateImages: true,
      },
      autoActivate: true,
    },
  })

  const watchAutoActivate = watchCreate('autoActivate')

  const submitCreate = async (values: IEditionsValuesCreate) => {
    try {
      const data = {
        editionName: values.name,
        migrationConfig: values.migrationConfig,
        autoActivate: values.autoActivate,
        activateDate:
          values.autoActivate || !values.activateDate
            ? null
            : new Date(values.activateDate).valueOf(),
        startDate: new Date(values.startDate).valueOf(),
        endDate: new Date(values.endDate).valueOf(),
        backstageEventCode: values.backstageEventCode,
      }
      const { status } = await createEditions(data)
      getData()
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Create Successful')
        resetCreate()
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  return (
    <div>
      <div>
        <h4 className="text-title-s text-accent-1 mb-4">Editions</h4>
      </div>
      <Card>
        <div className="flex flex-col p-3 space-y-7">
          {map(editionEditFields, (edition, index) => {
            return (
              <form id="settings-editions-edit-form" key={edition.id}>
                {openIndex !== edition.eId ? (
                  edition.active ? (
                    <FormGroup>
                      <div className="flex gap-2 w-full items-center">
                        <Input value={edition.name} type="text" disabled />
                        <Button
                          onClick={() => {
                            setOpenIndex(edition.eId)
                          }}
                          className="flex-1"
                          variant="secondary"
                          type="button"
                          disabled={!hasEditPermission}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          disabled={!hasEditPermission}
                          variant="secondary"
                        >
                          Current Edition
                        </Button>
                      </div>
                    </FormGroup>
                  ) : (
                    <FormGroup>
                      <div className="flex gap-2 w-full items-center">
                        <Input
                          value={edition.name}
                          type="text"
                          disabled={!hasEditPermission}
                        />
                        <Button
                          onClick={() => {
                            setOpenIndex(edition.eId)
                          }}
                          className="flex-1"
                          variant="secondary"
                          type="button"
                        >
                          Edit
                        </Button>
                        <Button
                          className="flex-1"
                          variant="secondary"
                          type="button"
                          onClick={() => handlePublish(edition.eId)}
                          disabled={!hasEditPermission}
                        >
                          Publish
                        </Button>
                        <Button
                          className="flex-1"
                          variant="destructive"
                          type="button"
                          onClick={() => {
                            setDeleteId(edition.eId)
                            setDeleteConfirmationModal(true)
                          }}
                          disabled={!hasEditPermission}
                        >
                          Delete
                        </Button>
                      </div>
                    </FormGroup>
                  )
                ) : (
                  <FormGroup>
                    <div className="flex gap-2 w-full items-center">
                      <Input value={edition.name} type="text" disabled />
                      <Button
                        onClick={() => {
                          handleCancel(edition.eId)
                        }}
                        className="flex-1"
                        variant="secondary"
                        type="button"
                        disabled={!hasEditPermission}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="flex-1"
                        variant="primary"
                        form="settings-editions-edit-form"
                        onClick={() => {
                          submitEdit(index)
                        }}
                        disabled={!hasEditPermission}
                      >
                        Save
                      </Button>
                    </div>
                  </FormGroup>
                )}
                {openIndex === edition.eId && (
                  <Card className="flex flex-col mt-2 gap-3 p-4">
                    <FormGroup label="Edition Name">
                      <Input
                        isInvalid={
                          !!errorsEdit?.editions?.[index]?.name?.message
                        }
                        {...registerEdit(`editions.${index}.name`)}
                        type="text"
                        disabled={!hasEditPermission}
                      />
                    </FormGroup>
                    <FormGroup label="Backstage Event Code">
                      <Input
                        isInvalid={
                          !!errorsEdit?.editions?.[index]?.backstageEventCode
                            ?.message
                        }
                        {...registerEdit(
                          `editions.${index}.backstageEventCode`,
                        )}
                        type="text"
                        disabled={!hasEditPermission}
                      />
                    </FormGroup>
                    <div className="flex items-center gap-2">
                      <FormGroup label="Starting Date">
                        <DateTimePicker
                          value={
                            watchEdit(`editions.${index}.startDate`)
                              ? new Date(
                                  watchEdit(`editions.${index}.startDate`),
                                )
                              : null
                          }
                          onChange={(date) => {
                            setValueEdit(
                              `editions.${index}.startDate`,
                              date ? formatDateTime(date.toISOString()) : '',
                            )
                          }}
                          hasError={
                            !!errorsEdit?.editions?.[index]?.startDate?.message
                          }
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                      <FormGroup label="End Date">
                        <DateTimePicker
                          value={
                            watchEdit(`editions.${index}.endDate`)
                              ? new Date(watchEdit(`editions.${index}.endDate`))
                              : null
                          }
                          onChange={(date) => {
                            setValueEdit(
                              `editions.${index}.endDate`,
                              date ? formatDateTime(date.toISOString()) : '',
                            )
                          }}
                          hasError={
                            !!errorsEdit?.editions?.[index]?.endDate?.message
                          }
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                    </div>
                  </Card>
                )}
              </form>
            )
          })}
          <form
            id="settings-editions-create-form"
            onSubmit={handleSubmitCreate(submitCreate)}
          >
            <Card className="flex flex-col p-4 gap-3">
              <FormGroup label="Next Edition">
                <Input
                  isInvalid={!!errorsCreate?.name?.message}
                  placeholder="Enter Edition Name"
                  {...registerCreate(`name`)}
                  type="text"
                  disabled={!hasEditPermission}
                />
              </FormGroup>
              <div className="flex items-center gap-2">
                <FormGroup label="Starting Date">
                  <DateTimePicker
                    value={
                      watchCreate('startDate')
                        ? new Date(watchCreate('startDate'))
                        : null
                    }
                    onChange={(date) => {
                      setValueCreate(
                        'startDate',
                        date ? formatDateTime(date.toISOString()) : '',
                      )
                    }}
                    hasError={!!errorsCreate?.startDate?.message}
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
                <FormGroup label="End Date">
                  <DateTimePicker
                    value={
                      watchCreate('endDate')
                        ? new Date(watchCreate('endDate'))
                        : null
                    }
                    onChange={(date) => {
                      setValueCreate(
                        'endDate',
                        date ? formatDateTime(date.toISOString()) : '',
                      )
                    }}
                    hasError={!!errorsCreate?.endDate?.message}
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
              </div>
              <FormGroup
                label="What to migrate:"
                labelClassName="text-title-xs"
              >
                <Card className="flex justify-between w-full gap-3 p-4">
                  <FormGroup label="Info Page">
                    <Checkbox
                      isInvalid={
                        !!errorsCreate?.migrationConfig?.migrateInfos?.message
                      }
                      {...registerCreate(`migrationConfig.migrateInfos`)}
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormGroup label="Stages (performances)">
                    <Checkbox
                      isInvalid={
                        !!errorsCreate?.migrationConfig?.migratePerformances
                          ?.message
                      }
                      {...registerCreate(`migrationConfig.migratePerformances`)}
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormGroup label="Partners">
                    <Checkbox
                      isInvalid={
                        !!errorsCreate?.migrationConfig?.migratePartners
                          ?.message
                      }
                      {...registerCreate(`migrationConfig.migratePartners`)}
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormGroup label="Ticket Categories">
                    <Checkbox
                      isInvalid={
                        !!errorsCreate?.migrationConfig?.migrateTicketCategories
                          ?.message
                      }
                      {...registerCreate(
                        `migrationConfig.migrateTicketCategories`,
                      )}
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormGroup label="Add-Ons">
                    <Checkbox
                      isInvalid={
                        !!errorsCreate?.migrationConfig?.migrateExtras?.message
                      }
                      {...registerCreate(`migrationConfig.migrateExtras`)}
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormGroup label="Videos">
                    <Checkbox
                      isInvalid={
                        !!errorsCreate?.migrationConfig?.migrateVideos?.message
                      }
                      {...registerCreate(`migrationConfig.migrateVideos`)}
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormGroup label="Images">
                    <Checkbox
                      isInvalid={
                        !!errorsCreate?.migrationConfig?.migrateImages?.message
                      }
                      {...registerCreate(`migrationConfig.migrateImages`)}
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                </Card>
              </FormGroup>
              <FormGroup label="Auto Activate">
                <Checkbox
                  isInvalid={!!errorsCreate?.autoActivate?.message}
                  {...registerCreate(`autoActivate`)}
                  disabled={!hasEditPermission}
                />
              </FormGroup>
              <FormGroup label="Publish Date">
                <DateTimePicker
                  value={
                    watchCreate('activateDate') &&
                    watchCreate('activateDate') !== ''
                      ? new Date(watchCreate('activateDate') as string)
                      : null
                  }
                  onChange={(date) => {
                    setValueCreate(
                      'activateDate',
                      date ? formatDateTime(date.toISOString()) : null,
                    )
                  }}
                  placeholder="Enter Publish Date"
                  hasError={!!errorsCreate?.activateDate?.message}
                  disabled={watchAutoActivate || !hasEditPermission}
                />
              </FormGroup>
              <FormGroup label="Backstage Event Code">
                <Input
                  isInvalid={!!errorsCreate?.backstageEventCode?.message}
                  {...registerCreate(`backstageEventCode`)}
                  type="text"
                  disabled={!hasEditPermission}
                />
              </FormGroup>
              <div className="flex justify-end border-t border-monochrome-120 pt-4">
                <Button
                  variant="primary"
                  form="settings-editions-create-form"
                  type="submit"
                  disabled={!hasEditPermission}
                >
                  Create
                </Button>
              </div>
            </Card>
          </form>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Edition'}
        deleteFunc={deleteFunc}
      />
    </div>
  )
}
